/**
 * Inspiration as to the technique to inject GA taken from :
 * https://github.com/mzuccaroli/angular-google-tag-manager
 * Having to write our own due to the lack of support for GTM Environments, which allow for us to deploy GTM/GA to preprod environments.
 * https://github.com/mzuccaroli/angular-google-tag-manager/issues/4
 */
import { Injectable, Inject, Optional } from '@angular/core';
import { ABTestService } from './ab-test.service';
import { PermissionsService } from '@app/modules/auth';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { VdoUser } from '../auth/models/vdo-user';

/**
 *
 */

export enum ZonarUIAnalyticsTokens {
  ID = 'googleTagManagerId',
  AUTH = 'googleTagManagerAuth',
  ENVIRONMENT = 'googleTagManagerEnvironment',
  GOID = 'googleOptimizeContainerId'
}
/**
 * Service that adds Google Tag Manager and Analytics into a Zonar application.
 * This requires the 'googleTagManagerId' injection token
 */
@Injectable({
  providedIn: 'any'
})
export class ZonarUiAnalyticsService {
  private isLoaded = false;
  private readonly gtmId: string;
  private readonly gtmAuth: string;
  private readonly gtmEnv: string;
  private readonly gtmUrlPrefix = 'https://www.googletagmanager.com';
  private browserGlobals = {
    windowRef(): any {
      return window;
    },
    documentRef(): any {
      return document;
    }
  };

  constructor(
    @Inject(ZonarUIAnalyticsTokens.ID) public googleTagManagerId: string,
    @Optional() @Inject(ZonarUIAnalyticsTokens.AUTH) public googleTagManagerAuth: string,
    @Optional() @Inject(ZonarUIAnalyticsTokens.ENVIRONMENT) public googleTagManagerEnvironment: string,
    private abTestService: ABTestService,
    private permissionsService: PermissionsService
  ) {
    this.gtmId = googleTagManagerId;
    this.gtmAuth = googleTagManagerAuth;
    this.gtmEnv = googleTagManagerEnvironment;
    this.addUserIdTag();
  }
  addUserIdTag() {
    this.permissionsService
      .getUser()
      .pipe(
        filter(user => Boolean(user?.id)),
        distinctUntilChanged()
      )
      .subscribe((user: VdoUser) => {
        const tag = {
          user_id: user.id,
          crm_id: user.id
        };
        this.pushTag(tag);
      });
  }
  /*tslint:disable:no-string-literal */
  public getDataLayer() {
    const window = this.browserGlobals.windowRef() as Window;
    window['dataLayer'] = window['dataLayer'] || [];
    return window['dataLayer'];
  }
  /*tslint:enable:no-string-literal */
  private pushOnDataLayer(obj: object) {
    const dataLayer = this.getDataLayer();
    dataLayer.push(obj);
  }

  // Not adding private declaration as we need to call this from unit tests.
  addGtmLocationSuffix(): string {
    let gtmNs = `id=${this.gtmId}`;
    if (this.gtmAuth && this.gtmEnv) {
      gtmNs += `&gtm_auth=${this.gtmAuth}&gtm_preview=${this.gtmEnv}`;
    }
    return gtmNs;
  }

  /**
   * Adds Google Tag manager to the DOM.
   */
  public addGtmToDom() {
    const doc = this.browserGlobals.documentRef() as Document;
    this.pushOnDataLayer({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
    const gtmScript = doc.createElement('script');
    gtmScript.id = 'GTMScript';
    // The types for HTMLElement don't understand that these properties do actually exist.
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLScriptElement
    // @ts-ignore
    gtmScript.async = true;
    // @ts-ignore
    gtmScript.src = `${this.gtmUrlPrefix}/gtm.js?${this.addGtmLocationSuffix()}`;
    doc.head.insertBefore(gtmScript, doc.head.firstChild);
    // EEEWWWWW!
    const iframe = doc.createElement('iframe');
    iframe.setAttribute('src', `${this.gtmUrlPrefix}/ns.html?${this.addGtmLocationSuffix()}`);
    // @ts-ignore
    iframe.async = true;
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    const noscript = doc.createElement('noscript');
    noscript.id = 'GTMiframe';
    noscript.appendChild(iframe);
    doc.body.insertBefore(noscript, doc.body.firstChild);

    this.isLoaded = true;
  }

  public pushTag(item: object) {
    if (!this.isLoaded) {
      this.addGtmToDom();
    }
    this.pushOnDataLayer(item);
  }
}
